import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Pencil, Trash2 } from 'lucide-react';
import { Button, DataTable, ErrorState, Loading, PageHeader, Panel, StatusBadge, useConfirm, useToast, type Column } from '@/components';
import { useCurrentUser } from '@/auth/AuthProvider';
import { READ_ONLY_ROLES } from '@/auth/types';
import { useFiscalYear } from '@/fiscal-year/FiscalYearProvider';
import { errorMessage } from '@/lib/api';
import { formatDate, formatNumber } from '@/lib/format';
import { useDeleteVessel, useVessel, type VesselCapacity, type VesselDetail, type VesselPerson } from './api';
import './vessels.css';

function Dl({ cols = 2, children }: { cols?: 1 | 2 | 3; children: ReactNode }) {
  return (
    <dl className="bs-dl" style={{ ['--dl-cols' as string]: cols }}>
      {children}
    </dl>
  );
}
function Item({ label, wide, children }: { label: string; wide?: boolean; children?: ReactNode }) {
  return (
    <div className={wide ? 'bs-dl__item is-wide' : 'bs-dl__item'}>
      <dt>{label}</dt>
      <dd>{children}</dd>
    </div>
  );
}
const period = (from: string | null, to: string | null) => (from || to ? `${formatDate(from) || '—'} 〜 ${formatDate(to) || '—'}` : '');

/** S-11 参加船名簿 詳細 */
export function VesselDetailPage() {
  const { id } = useParams();
  const vesselId = Number(id);
  const { data, isPending, error, refetch } = useVessel(Number.isInteger(vesselId) ? vesselId : null);
  if (isPending) return <Loading block />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  return <Detail v={data} />;
}

function Detail({ v }: { v: VesselDetail }) {
  const navigate = useNavigate();
  const user = useCurrentUser();
  const { yearPath } = useFiscalYear();
  const toast = useToast();
  const confirm = useConfirm();
  const del = useDeleteVessel();
  const canEdit = !READ_ONLY_ROLES.includes(user.organizationType);
  const listPath = yearPath('vessels');
  const isAdmin = user.organizationType === '基金';

  const onDelete = async () => {
    const ok = await confirm({
      title: '参加船を削除しますか？',
      message: `${v.name}（${v.registrationNo}）を削除します。\n事前連絡・速報などで使用されている場合は削除できません。この操作は取り消せません。`,
      tone: 'danger',
      confirmLabel: '削除する',
    });
    if (!ok) return;
    try {
      await del.mutateAsync(v.id);
      toast.success('参加船を削除しました', v.name);
      navigate(listPath);
    } catch (e) {
      toast.error('削除できませんでした', errorMessage(e));
    }
  };

  const personColumns: Column<VesselPerson>[] = [
    { key: 'name', header: '氏名', render: (p) => p.name },
    { key: 'period', header: '承認期間', width: 230, render: (p) => <span className="bs-num">{period(p.approvalFrom, p.approvalTo) || '—'}</span> },
  ];
  const capacityColumns: Column<VesselCapacity>[] = [
    { key: 'boardingCount', header: '乗船人員', numeric: true, width: 110 },
    { key: 'totalBoardingCount', header: '全乗船人員', numeric: true, width: 120 },
    { key: 'period', header: '承認期間', render: (c) => <span className="bs-num">{period(c.approvalFrom, c.approvalTo) || '—'}</span> },
  ];

  return (
    <>
      <PageHeader
        eyebrow="船舶・計画 ／ 参加船名簿"
        title={v.name}
        meta={v.isApproved ? <StatusBadge tone="success" dot>承認済み</StatusBadge> : <StatusBadge tone="warning" dot>未承認</StatusBadge>}
        description={`${v.businessOperatorName} ／ 名簿No ${v.displayOrder} ／ 参加船ID ${v.vesselNo} ／ 漁船登録番号 ${v.registrationNo}`}
        actions={
          <>
            <Button icon={<ArrowLeft size={16} />} onClick={() => navigate(listPath)}>一覧へ戻る</Button>
            {canEdit && (
              <>
                <Button variant="danger" icon={<Trash2 size={16} />} onClick={onDelete} loading={del.isPending}>削除</Button>
                <Button variant="primary" icon={<Pencil size={16} />} onClick={() => navigate(yearPath(`vessels/${v.id}/edit`))}>編集</Button>
              </>
            )}
          </>
        }
      />

      <Panel title="基本情報">
        <Dl>
          <Item label="事業実施者">{v.businessOperatorName}</Item>
          <Item label="氏名又は名称">{v.ownerName}</Item>
          <Item label="船名">{v.name}</Item>
          <Item label="船名（カナ）">{v.nameKana}</Item>
          <Item label="漁船登録番号"><span className="bs-num">{v.registrationNo}</span></Item>
          <Item label="船舶トン数"><span className="bs-num">{v.tonnage.toFixed(2)}</span> トン</Item>
          <Item label="馬力">{v.horsepower}</Item>
          <Item label="住所">{v.address}</Item>
          <Item label="燃油種類">{v.fuelType}</Item>
          <Item label="税算出">{v.taxRounding}</Item>
          <Item label="インボイス登録者">{v.isInvoiceRegistered ? '登録あり' : '登録なし'}</Item>
          {isAdmin && <Item label="共済対象">{v.isMutualAidTarget ? '対象' : '対象外'}</Item>}
          <Item label="承認期間（参加期間）" wide>{period(v.approvalFrom, v.approvalTo)}</Item>
        </Dl>
      </Panel>

      <Panel title="船舶電話">
        <Dl cols={1}>
          <Item label="船舶電話番号"><span className="bs-num">{v.phone}</span>{v.phone && period(v.phoneFrom, v.phoneTo) && <span>　（{period(v.phoneFrom, v.phoneTo)}）</span>}</Item>
        </Dl>
      </Panel>

      <Panel title="請求者情報" description="報告書兼請求書（別記3）・助成金領収書（別記4）に印字される情報です。">
        <Dl>
          <Item label="請求先宛先">{v.billingAddressee}</Item>
          <Item label="請求者名">{v.claimantName}</Item>
          <Item label="請求者住所">{v.claimantAddress}</Item>
          <Item label="請求者登録番号">{v.claimantInvoiceNo ? <span className="bs-num">T{v.claimantInvoiceNo}</span> : null}</Item>
          <Item label="用船料受領者">{v.charterFeeRecipient}</Item>
        </Dl>
      </Panel>

      <Panel title="船長" flush>
        <DataTable caption="船長" columns={personColumns} rows={v.captains} rowKey={(p) => p.id ?? p.name} dense empty="船長が登録されていません" />
      </Panel>
      <Panel title="乗組員" flush>
        <DataTable caption="乗組員" columns={personColumns} rows={v.crewMembers} rowKey={(p) => p.id ?? p.name} dense empty="乗組員は登録されていません" />
      </Panel>
      <Panel title="乗船人数" flush>
        <DataTable caption="乗船人数" columns={capacityColumns} rows={v.capacities} rowKey={(c) => c.id ?? c.boardingCount} dense empty="乗船人数が登録されていません" />
      </Panel>

      <Panel title="漁業種類">
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {v.fishingMethods.map((m) => (
            <StatusBadge key={m} tone="navy">{m}</StatusBadge>
          ))}
        </div>
      </Panel>

      <Panel title="計画日数" description="計画日数登録画面で一括入力します。" actions={canEdit && <Button size="sm" onClick={() => navigate(yearPath(`vessels/plan-days?name=${encodeURIComponent(v.name)}`))}>計画日数登録へ</Button>}>
        <div className="bs-vessel-plan">
          {[v.planDaysQ1, v.planDaysQ2, v.planDaysQ3, v.planDaysQ4].map((d, i) => (
            <div key={i} className="bs-vessel-plan__q"><span>第{i + 1}四半期</span><strong>{formatNumber(d)}</strong> 日</div>
          ))}
          <div className="bs-vessel-plan__q is-total"><span>合計</span><strong>{formatNumber(v.planDaysQ1 + v.planDaysQ2 + v.planDaysQ3 + v.planDaysQ4)}</strong> 日</div>
        </div>
      </Panel>

      {v.note && (
        <Panel title="備考">
          <p style={{ margin: 0, whiteSpace: 'pre-wrap' }}>{v.note}</p>
        </Panel>
      )}
    </>
  );
}
